import { CreditcardService } from '@/services/api/creditcard'
import { CreditcardPaymentProviderService } from '@/services/payment/creditcard-payment-provider/creditcard-payment-provider-service'
import { ThreeDSecureExtraProfile } from '@spa/components/organisms/paid-membership/creditcard-extra-profile/extra-profile'
import { CreditcardFormState } from '@spa/components/organisms/payment/creditcard/creditcardFormState'
import { User } from '@spa/store/modules/user/types'
import { Option } from 'fp-ts/lib/Option'
import { ActionRequest } from '../action/action'

export interface CreditcardRepository {
  /** TODO:
   * registerメソッドが3Dセキュアで使用される場合、最終的に遷移から戻ってきてエンドユーザに表示するリダイレクト先URLを可変的に渡す。
   * また3D認証HTML画面以降、A09 API等でエラーになった場合に、同じ最終リダイレクト先でもエラーとして表示すべきかどうかのフラグも渡す。
   */
  register(creditcard: CreditcardFormState, user: User, context: RegisterContext): Promise<ActionRequest>
  remove(creditcardId: string): Promise<void>
}

export type CreditcardRegisterService<T extends CreditcardPaymentProviderService> =
  Pick<T, 'loadScript' | 'createRegisterCardPayload'>
export abstract class FrontAPICreditCardRepository<
  Cs extends CreditcardService,
  Cps extends CreditcardPaymentProviderService
> implements CreditcardRepository {
  protected scriptLoading: Promise<void>

  public constructor(
    protected readonly creditCardService: Cs,
    protected readonly cardPaymentProvider: CreditcardRegisterService<Cps>
    ) {
    /**
     * 当初、#register内部でスクリプトロードを行うようにする予定だったが、
     * そのかたちで実装すると以下のようなエラーが発生して、処理が進まない(catchにも引っかからない模様. 別プロセスで発生している？)
     * ```
     * Failed to execute 'postMessage' on 'DOMWindow':
     * The target origin provided ('https://ptwebcollect.jp')
     * does not match the recipient window's origin ('{実行時のドメイン}').
     * ```
     * 既存実装では問題なく動いていたことを考えると、おそらく、スクリプトを取得してからある程度の時間(程度は不明)が経過した後なら大丈夫？
     * ということで、一旦、既存実装と同様に初期化タイミングでのスクリプトロードに変更した.
     * これで確実に大丈夫なわけではないが、人間の操作速度の範囲内なら、これまで動作してきたことから回避可能かと思われる.
     *
     * @see https://github.com/my-color/front/pull/6312 このワークアラウンドを入れたPR
     */
    this.scriptLoading = this.cardPaymentProvider.loadScript()
  }

  abstract register(creditcard: CreditcardFormState, user: User, context: RegisterContext): Promise<ActionRequest>

  async remove(creditcardId: string): Promise<void> {
    const result = await this.creditCardService.remove(creditcardId)
    result.fold(
      (error) => {
        throw error
      },
      () => undefined
    )
  }
}

export interface RegisterContext {
  /**
   * 3D認証画面の後に最終的にエンドユーザに表示されるGP上のリダイレクト先。
   * リポジトリの設計上、カード登録は3Dセキュアを利用するかどうかについて透過的であるため、
   * 3Dセキュアを利用しない場合でもOptionをNoneで渡す。
   */
  readonly threeDSecureRedirectUrl: Option<URL>
  readonly threeDSecureExtraProfile: Option<ThreeDSecureExtraProfile>
}
